import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plane, 
  Hotel, 
  Car, 
  FileText, 
  Calendar, 
  MapPin, 
  DollarSign, 
  Plus, 
  Search, 
  Filter,
  Edit,
  Eye,
  Phone,
  Clock,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  Globe,
  CreditCard,
  Users,
  Shield,
  Star,
  ArrowLeft,
  Check,
  Trash2,
  X,
  Camera,
  Upload,
  Download,
  Share2,
  Bookmark,
  Navigation,
  Wifi,
  Car as CarIcon,
  Train,
  Bus,
  Ship
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { travelService } from '../../lib/travelService';
import { format, addDays, isBefore, isAfter, differenceInDays } from 'date-fns';
import { toast } from 'react-hot-toast';

interface Trip {
  id: string;
  title: string;
  destination?: string;
  start_date: string;
  end_date: string;
  status: 'planned' | 'in_progress' | 'completed' | 'cancelled';
  budget?: number;
  currency?: string;
  description?: string;
  image_url?: string;
  created_at: string;
  updated_at: string;
}

interface Accommodation {
  id: string;
  trip_id: string;
  name: string;
  type: 'hotel' | 'apartment' | 'house' | 'hostel' | 'resort' | 'other';
  address: string;
  check_in_date: string;
  check_out_date: string;
  confirmation_number?: string;
  total_cost?: number;
  currency?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface Transportation {
  id: string;
  trip_id: string;
  type: 'flight' | 'train' | 'bus' | 'car' | 'ferry' | 'other';
  provider: string;
  departure_location: string;
  arrival_location: string;
  departure_time: string;
  arrival_time: string;
  confirmation_number?: string;
  total_cost?: number;
  currency?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface TravelDocument {
  id: string;
  trip_id?: string;
  user_id: string;
  type: 'passport' | 'visa' | 'insurance' | 'vaccination' | 'other';
  name: string;
  file_url?: string;
  document_number?: string;
  issuing_country?: string;
  issue_date?: string;
  expiry_date?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export function MobileTravelDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'trips' | 'accommodations' | 'transportation' | 'documents'>('trips');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Data state
  const [trips, setTrips] = useState<Trip[]>([]);
  const [accommodations, setAccommodations] = useState<Accommodation[]>([]);
  const [transportation, setTransportation] = useState<Transportation[]>([]);
  const [documents, setDocuments] = useState<TravelDocument[]>([]);
  
  // UI state
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [showTripDetails, setShowTripDetails] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const [tripsData, accommodationsData, transportationData, documentsData] = await Promise.all([
        travelService.trips.getAll(),
        travelService.accommodations.getAll(),
        travelService.transportation.getAll(),
        travelService.documents.getAll()
      ]);
      
      setTrips(tripsData);
      setAccommodations(accommodationsData);
      setTransportation(transportationData);
      setDocuments(documentsData);
    } catch (err) {
      console.error('Error loading travel data:', err);
      setError('Failed to load travel data');
      toast.error('Failed to load travel data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planned': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'planned': return <Calendar size={16} />;
      case 'in_progress': return <Clock size={16} />;
      case 'completed': return <CheckCircle size={16} />;
      case 'cancelled': return <X size={16} />;
      default: return <Calendar size={16} />;
    }
  };

  const getTransportationIcon = (type: string) => {
    switch (type) {
      case 'flight': return <Plane size={16} />;
      case 'train': return <Train size={16} />;
      case 'bus': return <Bus size={16} />;
      case 'car': return <CarIcon size={16} />;
      case 'ferry': return <Ship size={16} />;
      default: return <Navigation size={16} />;
    }
  };

  const getAccommodationIcon = (type: string) => {
    switch (type) {
      case 'hotel': return <Hotel size={16} />;
      case 'apartment': return <Users size={16} />;
      case 'house': return <Shield size={16} />;
      case 'hostel': return <Users size={16} />;
      case 'resort': return <Star size={16} />;
      default: return <Hotel size={16} />;
    }
  };

  const getDaysUntilTrip = (startDate: string) => {
    const days = differenceInDays(new Date(startDate), new Date());
    if (days < 0) return 'Past';
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    return `${days} days`;
  };

  const filteredTrips = trips.filter(trip => {
    const matchesSearch = trip.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (trip.destination && trip.destination.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || trip.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const filteredAccommodations = accommodations.filter(acc => {
    const matchesSearch = acc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         acc.address.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const filteredTransportation = transportation.filter(trans => {
    const matchesSearch = trans.provider.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         trans.departure_location.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         trans.arrival_location.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (doc.document_number && doc.document_number.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesSearch;
  });

  const handleTripSelect = (trip: Trip) => {
    setSelectedTrip(trip);
    setShowTripDetails(true);
  };

  const handleAddTrip = () => {
    navigate('/travel/add-trip');
  };

  const handleEditTrip = (trip: Trip) => {
    navigate(`/travel/edit-trip/${trip.id}`);
  };

  const handleDeleteTrip = async (tripId: string) => {
    if (!confirm('Are you sure you want to delete this trip?')) return;
    
    try {
      await travelService.trips.delete(tripId);
      setTrips(trips.filter(t => t.id !== tripId));
      toast.success('Trip deleted successfully');
    } catch (error) {
      console.error('Error deleting trip:', error);
      toast.error('Failed to delete trip');
    }
  };

  const renderTrips = () => (
    <div className="space-y-4">
      {filteredTrips.length === 0 ? (
        <div className="text-center py-8">
          <Plane className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No trips found</h3>
          <p className="text-gray-500 mb-4">Start planning your next adventure!</p>
          <Button onClick={handleAddTrip} className="bg-idecide-green hover:bg-idecide-green/90">
            <Plus size={16} className="mr-2" />
            Add Trip
          </Button>
        </div>
      ) : (
        filteredTrips.map((trip) => (
          <motion.div
            key={trip.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 text-lg">{trip.title}</h3>
                  {trip.destination && (
                    <div className="flex items-center text-gray-600 text-sm mt-1">
                      <MapPin size={14} className="mr-1" />
                      {trip.destination}
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(trip.status)}`}>
                    {trip.status.replace('_', ' ')}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditTrip(trip)}
                  >
                    <Edit size={14} />
                  </Button>
                </div>
              </div>

              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Calendar size={14} className="mr-2" />
                    <span>
                      {format(new Date(trip.start_date), 'MMM dd')} - {format(new Date(trip.end_date), 'MMM dd, yyyy')}
                    </span>
                  </div>
                  <span className="text-idecide-green font-medium">
                    {getDaysUntilTrip(trip.start_date)}
                  </span>
                </div>

                {trip.budget && (
                  <div className="flex items-center">
                    <DollarSign size={14} className="mr-2" />
                    <span>Budget: {trip.currency} {trip.budget.toLocaleString()}</span>
                  </div>
                )}

                {trip.description && (
                  <p className="text-gray-500 line-clamp-2">{trip.description}</p>
                )}
              </div>

              <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <div className="flex items-center">
                    <Hotel size={12} className="mr-1" />
                    <span>{accommodations.filter(a => a.trip_id === trip.id).length}</span>
                  </div>
                  <div className="flex items-center">
                    <Plane size={12} className="mr-1" />
                    <span>{transportation.filter(t => t.trip_id === trip.id).length}</span>
                  </div>
                  <div className="flex items-center">
                    <FileText size={12} className="mr-1" />
                    <span>{documents.filter(d => d.trip_id === trip.id).length}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTripSelect(trip)}
                  >
                    <Eye size={14} className="mr-1" />
                    View
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteTrip(trip.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        ))
      )}
    </div>
  );

  const renderAccommodations = () => (
    <div className="space-y-4">
      {filteredAccommodations.length === 0 ? (
        <div className="text-center py-8">
          <Hotel className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No accommodations</h3>
          <p className="text-gray-500">Add accommodations to your trips</p>
        </div>
      ) : (
        filteredAccommodations.map((accommodation) => (
          <motion.div
            key={accommodation.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center mb-1">
                  {getAccommodationIcon(accommodation.type)}
                  <span className="ml-2 text-xs text-gray-500 uppercase">{accommodation.type}</span>
                </div>
                <h3 className="font-semibold text-gray-900">{accommodation.name}</h3>
                <p className="text-sm text-gray-600 mt-1">{accommodation.address}</p>
              </div>
            </div>

            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center justify-between">
                <span>Check-in: {format(new Date(accommodation.check_in_date), 'MMM dd, yyyy')}</span>
                <span>Check-out: {format(new Date(accommodation.check_out_date), 'MMM dd, yyyy')}</span>
              </div>
              
              {accommodation.confirmation_number && (
                <div className="flex items-center">
                  <CheckCircle size={14} className="mr-2 text-green-600" />
                  <span>Confirmation: {accommodation.confirmation_number}</span>
                </div>
              )}
              
              {accommodation.total_cost && (
                <div className="flex items-center">
                  <DollarSign size={14} className="mr-2" />
                  <span>{accommodation.currency} {accommodation.total_cost.toLocaleString()}</span>
                </div>
              )}
            </div>
          </motion.div>
        ))
      )}
    </div>
  );

  const renderTransportation = () => (
    <div className="space-y-4">
      {filteredTransportation.length === 0 ? (
        <div className="text-center py-8">
          <Plane className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No transportation</h3>
          <p className="text-gray-500">Add transportation to your trips</p>
        </div>
      ) : (
        filteredTransportation.map((transport) => (
          <motion.div
            key={transport.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center mb-1">
                  {getTransportationIcon(transport.type)}
                  <span className="ml-2 text-xs text-gray-500 uppercase">{transport.type}</span>
                </div>
                <h3 className="font-semibold text-gray-900">{transport.provider}</h3>
                <div className="flex items-center text-sm text-gray-600 mt-1">
                  <span>{transport.departure_location}</span>
                  <ChevronRight size={14} className="mx-2" />
                  <span>{transport.arrival_location}</span>
                </div>
              </div>
            </div>

            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Clock size={14} className="mr-2" />
                  <span>Departure: {format(new Date(transport.departure_time), 'MMM dd, HH:mm')}</span>
                </div>
                <div className="flex items-center">
                  <Clock size={14} className="mr-2" />
                  <span>Arrival: {format(new Date(transport.arrival_time), 'MMM dd, HH:mm')}</span>
                </div>
              </div>
              
              {transport.confirmation_number && (
                <div className="flex items-center">
                  <CheckCircle size={14} className="mr-2 text-green-600" />
                  <span>Confirmation: {transport.confirmation_number}</span>
                </div>
              )}
              
              {transport.total_cost && (
                <div className="flex items-center">
                  <DollarSign size={14} className="mr-2" />
                  <span>{transport.currency} {transport.total_cost.toLocaleString()}</span>
                </div>
              )}
            </div>
          </motion.div>
        ))
      )}
    </div>
  );

  const renderDocuments = () => (
    <div className="space-y-4">
      {filteredDocuments.length === 0 ? (
        <div className="text-center py-8">
          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No documents</h3>
          <p className="text-gray-500">Add travel documents for your trips</p>
        </div>
      ) : (
        filteredDocuments.map((document) => (
          <motion.div
            key={document.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center mb-1">
                  <Shield size={16} className="mr-2" />
                  <span className="text-xs text-gray-500 uppercase">{document.type}</span>
                </div>
                <h3 className="font-semibold text-gray-900">{document.name}</h3>
                {document.document_number && (
                  <p className="text-sm text-gray-600 mt-1">#{document.document_number}</p>
                )}
              </div>
            </div>

            <div className="space-y-2 text-sm text-gray-600">
              {document.issuing_country && (
                <div className="flex items-center">
                  <Globe size={14} className="mr-2" />
                  <span>{document.issuing_country}</span>
                </div>
              )}
              
              {document.issue_date && document.expiry_date && (
                <div className="flex items-center justify-between">
                  <span>Issue: {format(new Date(document.issue_date), 'MMM dd, yyyy')}</span>
                  <span>Expiry: {format(new Date(document.expiry_date), 'MMM dd, yyyy')}</span>
                </div>
              )}
              
              {document.file_url && (
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm">
                    <Download size={14} className="mr-1" />
                    Download
                  </Button>
                  <Button variant="outline" size="sm">
                    <Share2 size={14} className="mr-1" />
                    Share
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        ))
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-idecide-green mx-auto mb-4"></div>
          <p className="text-gray-600">Loading travel data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading data</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <Button onClick={loadData} className="bg-idecide-green hover:bg-idecide-green/90">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-4 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Travel Management</h1>
            <p className="text-sm text-gray-600">Plan and organize your trips</p>
          </div>
          <Button onClick={handleAddTrip} className="bg-idecide-green hover:bg-idecide-green/90">
            <Plus size={16} className="mr-2" />
            Add Trip
          </Button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="px-4 py-3 bg-white border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search trips, accommodations, transportation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-idecide-green focus:border-transparent"
            />
          </div>
          <Button variant="outline" size="sm">
            <Filter size={16} />
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="flex space-x-1 px-4">
          {[
            { id: 'trips', label: 'Trips', icon: Plane, count: trips.length },
            { id: 'accommodations', label: 'Accommodations', icon: Hotel, count: accommodations.length },
            { id: 'transportation', label: 'Transportation', icon: Car, count: transportation.length },
            { id: 'documents', label: 'Documents', icon: FileText, count: documents.length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex-1 flex flex-col items-center py-3 px-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'text-idecide-green bg-idecide-green/10'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon size={16} className="mb-1" />
              <span>{tab.label}</span>
              {tab.count > 0 && (
                <span className="ml-1 text-xs bg-gray-200 text-gray-600 rounded-full px-1.5 py-0.5">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'trips' && renderTrips()}
            {activeTab === 'accommodations' && renderAccommodations()}
            {activeTab === 'transportation' && renderTransportation()}
            {activeTab === 'documents' && renderDocuments()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Trip Details Modal */}
      <AnimatePresence>
        {showTripDetails && selectedTrip && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowTripDetails(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">{selectedTrip.title}</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowTripDetails(false)}
                  >
                    <X size={16} />
                  </Button>
                </div>

                <div className="space-y-4">
                  {selectedTrip.destination && (
                    <div className="flex items-center text-gray-600">
                      <MapPin size={16} className="mr-2" />
                      <span>{selectedTrip.destination}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-gray-600">
                      <Calendar size={16} className="mr-2" />
                      <span>
                        {format(new Date(selectedTrip.start_date), 'MMM dd')} - {format(new Date(selectedTrip.end_date), 'MMM dd, yyyy')}
                      </span>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedTrip.status)}`}>
                      {selectedTrip.status.replace('_', ' ')}
                    </span>
                  </div>

                  {selectedTrip.budget && (
                    <div className="flex items-center text-gray-600">
                      <DollarSign size={16} className="mr-2" />
                      <span>Budget: {selectedTrip.currency} {selectedTrip.budget.toLocaleString()}</span>
                    </div>
                  )}

                  {selectedTrip.description && (
                    <p className="text-gray-600">{selectedTrip.description}</p>
                  )}

                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="font-medium text-gray-900 mb-3">Trip Summary</h3>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-semibold text-idecide-green">
                          {accommodations.filter(a => a.trip_id === selectedTrip.id).length}
                        </div>
                        <div className="text-xs text-gray-500">Accommodations</div>
                      </div>
                      <div>
                        <div className="text-2xl font-semibold text-idecide-green">
                          {transportation.filter(t => t.trip_id === selectedTrip.id).length}
                        </div>
                        <div className="text-xs text-gray-500">Transportation</div>
                      </div>
                      <div>
                        <div className="text-2xl font-semibold text-idecide-green">
                          {documents.filter(d => d.trip_id === selectedTrip.id).length}
                        </div>
                        <div className="text-xs text-gray-500">Documents</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-2 pt-4">
                    <Button
                      onClick={() => {
                        setShowTripDetails(false);
                        handleEditTrip(selectedTrip);
                      }}
                      className="flex-1 bg-idecide-green hover:bg-idecide-green/90"
                    >
                      <Edit size={16} className="mr-2" />
                      Edit Trip
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowTripDetails(false)}
                      className="flex-1"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
} 